import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import useFileUpload from "../../../../../../hooks/dataSenders/singleUpload";

const AddDocumentModal = ({
  show,
  handleClose,
  projectSetup,
  setProjectSetup,
  setLoader,
  editingIndex = null, // null means adding new, number means editing
}) => {
  const fileInputRef = useRef(null);
  const { uploadFile } = useFileUpload();

  // Local state for document
  const [documentData, setDocumentData] = useState({
    name: "",
    file: null,
  });

  const [documentFile, setDocumentFile] = useState(null);
  const [fileName, setFileName] = useState("");

  // Validation errors
  const [errors, setErrors] = useState({
    name: "",
    file: "",
  });

  // ✅ Load existing document data when editing
  useEffect(() => {
    if (show && editingIndex !== null) {
      const existingDoc = projectSetup.documents.value[editingIndex];
      setDocumentData({
        name: existingDoc.name || "",
        file: existingDoc.file || null,
      });
      if (existingDoc.file) {
        setDocumentFile({ name: existingDoc.name || "Document" });
        setFileName(existingDoc.name || "");
      }
    } else if (show && editingIndex === null) {
      resetForm();
    }
  }, [show, editingIndex, projectSetup.documents.value]);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "application/pdf") {
      setErrors((prev) => ({
        ...prev,
        file: "Only PDF files are allowed",
      }));
      e.target.value = ""; // Clear the input
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        file: "File size must be less than 10MB",
      }));
      e.target.value = ""; // Clear the input
      return;
    }

    try {
      setLoader(true);

      // Upload file
      const url = await uploadFile(file, setLoader);

      setDocumentFile(file);
      setFileName(file.name);
      setDocumentData((prev) => ({ ...prev, file: url }));

      // Clear file error
      setErrors((prev) => ({ ...prev, file: "" }));

      setLoader(false);
    } catch (error) {
      console.error("Upload error:", error);
      setErrors((prev) => ({
        ...prev,
        file: "Failed to upload document. Please try again.",
      }));
      setLoader(false);
    }
  };

  const removeDocument = () => {
    setDocumentFile(null);
    setFileName("");
    setDocumentData((prev) => ({ ...prev, file: null }));
  };

  const handleChange = (field, value) => {
    setDocumentData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: "",
      file: "",
    };

    let isValid = true;

    // Title - Required
    if (!documentData.name || documentData.name.trim() === "") {
      newErrors.name = "Document title is required";
      isValid = false;
    }

    // File - Required
    if (!documentData.file) {
      newErrors.file = "Document file is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAdd = () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    if (editingIndex !== null) {
      // Edit existing document
      const updatedDocuments = [...projectSetup.documents.value];
      updatedDocuments[editingIndex] = {
        name: documentData.name,
        file: documentData.file,
      };

      setProjectSetup((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          value: updatedDocuments,
        },
      }));
    } else {
      // Helper function to check if a document is empty
      const isDocumentEmpty = (doc) => {
        return (!doc.name || doc.name.trim() === "") && !doc.file;
      };

      // Check if first document (index 0) is empty
      const isFirstDocumentEmpty =
        projectSetup.documents.value.length > 0 &&
        isDocumentEmpty(projectSetup.documents.value[0]);

      const newDocument = {
        name: documentData.name,
        file: documentData.file,
      };

      if (isFirstDocumentEmpty) {
        // Replace the first empty document at index 0
        const updatedDocuments = [...projectSetup.documents.value];
        updatedDocuments[0] = newDocument;

        setProjectSetup((prev) => ({
          ...prev,
          documents: {
            ...prev.documents,
            value: updatedDocuments,
          },
        }));
      } else {
        // Add new document normally
        setProjectSetup((prev) => ({
          ...prev,
          documents: {
            ...prev.documents,
            value: [...prev.documents.value, newDocument],
          },
        }));
      }
    }

    // Close modal and reset
    handleClose();
    resetForm();
  };

  const resetForm = () => {
    setDocumentData({
      name: "",
      file: null,
    });
    setDocumentFile(null);
    setFileName("");
    setErrors({
      name: "",
      file: "",
    });
  };

  const handleCancel = () => {
    resetForm();
    handleClose();
  };

  return (
    <Modal
      className="publish-modall"
      show={show}
      onHide={handleCancel}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {editingIndex !== null ? "Edit Document" : "Add Document"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="publish_modal">
          <div className="addddoc">
            <div className="doc_inner">
              <h3>Documents</h3>
              <p>Format: PDF, Max Size: 10MB</p>
            </div>

            <div className="uploaddoc">
              <h4>
                {documentFile
                  ? fileName || documentFile.name
                  : "Upload Document"}
              </h4>

              {!documentFile && (
                <>
                  <button onClick={openFilePicker}>Upload Document</button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".pdf"
                    hidden
                    onChange={onFileChange}
                  />
                </>
              )}

              {documentFile && (
                <button
                  className="deletebuttonsss"
                  onClick={removeDocument}
                  style={{ marginLeft: "10px" }}
                >
                  Delete
                </button>
              )}
            </div>
            {errors.file && (
              <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                {errors.file}
              </p>
            )}

            <div>
              <h3>
                Title <span style={{ color: "red" }}>*</span>
              </h3>
              <input
                type="text"
                placeholder="Document title"
                value={documentData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={errors.name ? "error" : ""}
              />
              {errors.name && (
                <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {errors.name}
                </p>
              )}
            </div>
          </div>

          <div className="publish_buttons">
            <button onClick={handleCancel} className="cancel_button">
              Cancel
            </button>
            <button className="confirm_button" onClick={handleAdd}>
              {editingIndex !== null ? "Update" : "Add"}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddDocumentModal;
